import React, { createContext, useContext, useState, ReactNode } from 'react'
import type { CalendarEvent } from '../types'

export type ClipboardOperation = 'copy' | 'cut' | null

export interface ClipboardState {
  events: CalendarEvent[]
  operation: ClipboardOperation
}

interface CalendarClipboardContextValue {
  clipboard: ClipboardState
  copyEvents: (events: CalendarEvent[]) => void
  cutEvents: (events: CalendarEvent[]) => void
  pasteEvents: (targetDate: Date, targetHour?: number, targetMinute?: number) => CalendarEvent[]
  clearClipboard: () => void
  hasClipboard: boolean
}

const CalendarClipboardContext = createContext<CalendarClipboardContextValue | undefined>(
  undefined
)

interface CalendarClipboardProviderProps {
  children: ReactNode
}

export const CalendarClipboardProvider: React.FC<CalendarClipboardProviderProps> = ({
  children,
}) => {
  const [clipboard, setClipboard] = useState<ClipboardState>({
    events: [],
    operation: null,
  })

  const copyEvents = (events: CalendarEvent[]) => {
    setClipboard({
      events: events.map((e) => ({ ...e })), // Clone events
      operation: 'copy',
    })
  }

  const cutEvents = (events: CalendarEvent[]) => {
    setClipboard({
      events: events.map((e) => ({ ...e })), // Clone events
      operation: 'cut',
    })
  }

  const pasteEvents = (
    targetDate: Date,
    targetHour?: number,
    targetMinute?: number
  ): CalendarEvent[] => {
    if (clipboard.events.length === 0) return []

    const newEvents: CalendarEvent[] = []

    // Calculate time offset if specific time is provided
    let timeOffset = 0
    if (targetHour !== undefined && targetMinute !== undefined) {
      const firstEvent = clipboard.events[0]
      const firstEventStart = new Date(firstEvent.start)
      const targetTime = targetHour * 60 + targetMinute
      const originalTime =
        firstEventStart.getHours() * 60 + firstEventStart.getMinutes()
      timeOffset = targetTime - originalTime
    }

    for (const event of clipboard.events) {
      const originalStart = new Date(event.start)

      // Create new start time
      const newStart = new Date(targetDate)
      newStart.setHours(
        originalStart.getHours(),
        originalStart.getMinutes() + timeOffset,
        0,
        0
      )

      // Create new event with modified start time and new ID
      const newEvent: CalendarEvent = {
        ...event,
        id: `${event.id}-copy-${Date.now()}-${Math.random()}`, // Generate unique ID
        start: newStart,
        end: new Date(newStart.getTime() + event.duration * 60000),
      }

      newEvents.push(newEvent)
    }

    // If it was a cut operation, clear the clipboard
    if (clipboard.operation === 'cut') {
      setClipboard({ events: [], operation: null })
    }

    return newEvents
  }

  const clearClipboard = () => {
    setClipboard({ events: [], operation: null })
  }

  const hasClipboard = clipboard.events.length > 0

  return (
    <CalendarClipboardContext.Provider
      value={{
        clipboard,
        copyEvents,
        cutEvents,
        pasteEvents,
        clearClipboard,
        hasClipboard,
      }}
    >
      {children}
    </CalendarClipboardContext.Provider>
  )
}

export const useCalendarClipboard = () => {
  const context = useContext(CalendarClipboardContext)
  if (context === undefined) {
    throw new Error('useCalendarClipboard must be used within a CalendarClipboardProvider')
  }
  return context
}
